import type React from 'react';

import { IconLockClosedFilled } from '@zillow/constellation-icons';
import { AdornedInput, Box, FormField, FormHelp, Heading, Input, Label, Textarea } from '@zillow/constellation-10';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import PeopleInformation from './PeopleInformation';
import PhoneInput from '../../Widgets/PhoneInput';
import { LANDLORD_INFORMATION, PERSON } from '../../../constants/index';
import { PERSON_TYPE } from '../../../constants/stringConstants';
import type { PeopleSection } from '../../../models/index';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: PeopleSection;
  setSection: (section: PeopleSection) => void;
  setSectionValues: (keyValuePairs: object) => void;
};

export function LandlordInformation_v2({ section, setSection, setSectionValues }: Props): JSX.Element {
  const { user } = useRentalManagerUser();
  const { email: landlordEmail, firstName: landlordFirstName, lastName: landlordLastName } = user;
  const maxPeople = 1;

  function onFirstNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME]: value,
    });
  }

  function onLastNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME]: value,
    });
  }

  function onAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.ADDRESS]: value,
    });
  }

  function onPhoneNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs" css={{ marginBottom: 'layout.tight' }}>
        Who is the landlord?
      </Heading>
      <Box
        className={FULLSTORY_EXCLUDE}
        css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'layout.tight' }}
      >
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            label={<Label id="landlord-first-name">{PERSON.LABEL.FIRST_NAME}</Label>}
            control={
              <Input
                aria-labelledby="landlord-first-name"
                value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME)}
                onChange={onFirstNameChange}
              />
            }
          />
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            label={<Label id="landlord-last-name">{PERSON.LABEL.LAST_NAME}</Label>}
            control={
              <Input
                aria-labelledby="landlord-last-name"
                value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME)}
                onChange={onLastNameChange}
              />
            }
          />
        </Box>
        {section.hasField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS) && (
          <Box css={{ gridColumnEnd: 'span 12' }}>
            <FormField
              required
              error={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                  .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)
                  ? true
                  : false
              }
              label={<Label id="landlord-mailing-address">{PERSON.LABEL.MAILING_ADDRESS_IMPORTANT}</Label>}
              control={
                <Textarea
                  css={{ resize: 'vertical' }}
                  aria-labelledby="landlord-mailing-address"
                  value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)}
                  onChange={onAddressChange}
                />
              }
              description={
                section
                  .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                  .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? (
                  <FormHelp>
                    {section
                      .getField(LANDLORD_INFORMATION.FIELD_NAME.ADDRESS)
                      .showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}
                  </FormHelp>
                ) : (
                  <FormHelp>Street, city, state, ZIP code</FormHelp>
                )
              }
            />
          </Box>
        )}
        <Box css={{ gridColumnEnd: 'span 12' }}>
          <FormField
            required
            label={<Label>{PERSON.LABEL.EMAIL}</Label>}
            control={
              <AdornedInput.Root readOnly>
                <AdornedInput.Input defaultValue={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.EMAIL)} />
                <AdornedInput.Adornment>
                  <IconLockClosedFilled />
                </AdornedInput.Adornment>
              </AdornedInput.Root>
            }
          />
        </Box>
        {section.hasField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER) && (
          <Box css={{ gridColumnEnd: 'span 12' }}>
            <FormField
              required
              error={section.getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER).hasError}
              label={<Label id="landlord-phone-number">{PERSON.LABEL.PHONE_NUMBER}</Label>}
              control={
                <PhoneInput
                  aria-labelledby="landlord-phone-number"
                  value={section.getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER)}
                  onChange={onPhoneNumberChange}
                  error={section.getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER).hasError}
                />
              }
              description={
                section.getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER).hasError && (
                  <FormHelp>
                    {section.getField(LANDLORD_INFORMATION.FIELD_NAME.TELEPHONE_NUMBER).getErrorMessage()}
                  </FormHelp>
                )
              }
            />
          </Box>
        )}
      </Box>
      <Box css={{ marginTop: 'layout.tight' }}>
        <PeopleInformation
          isOptional
          maximumPeople={maxPeople}
          initialPeople={0}
          personType={PERSON_TYPE.COLANDLORD}
          setSectionValues={setSectionValues}
          setSection={setSection}
          section={section}
          label="Does anyone else co-own the property?"
          yesAndNoText="Most states require that you include all co-owners as co-landlords on your lease."
          initialEmail={landlordEmail}
          initialFirstName={landlordFirstName}
          initialLastName={landlordLastName}
        />
      </Box>
    </>
  );
}

export default LandlordInformation_v2;

LandlordInformation_v2.description = 'Colandlord information in addition to landlord information';
